import { Fragment, UIEvent, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BiPlus } from 'react-icons/bi';
import { usePopper } from 'react-popper';
import { Portal } from 'react-portal';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import { SpaceDiscriminator, SpaceHierarchyReadModel } from '@client/api';
import { IconButton, isDefinedString } from '@client/toolkit';
import { Menu, Transition } from '@headlessui/react';

import { PortfolioPaths } from '../../portfolio.router';

interface SpaceItemAddButtonProps {
  item: SpaceHierarchyReadModel | undefined;
}

export function SpaceItemAddButton({ item }: SpaceItemAddButtonProps) {
  const { t: tType } = useTranslation('type');
  const { buildingId } = useParams<{ buildingId: string }>();

  const navigate = useNavigate();

  const discriminator = useMemo(() => item?.discriminator, [item?.discriminator]);

  const popperElRef = useRef(null);
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-end',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          boundary: 'clippingParents',
        },
      },
      {
        name: 'flip',
        options: {
          allowedAutoPlacements: ['bottom-end'],
          fallbackPlacements: ['bottom-end', 'top-start'],
          altBoundary: true,
        },
      },
    ],
  });

  const handleSelectOption = (event: UIEvent, option: SpaceDiscriminator) => {
    event.stopPropagation();

    if (item == null || !isDefinedString(item.id)) {
      return;
    }

    if (discriminator === 'Building') {
      navigate(generatePath(PortfolioPaths.BuildingChildCreate, { buildingId, createDiscriminator: option }));
      return;
    }

    navigate(
      generatePath(PortfolioPaths.SpaceChildCreate, {
        buildingId,
        spaceId: item.id,
        discriminator,
        createDiscriminator: option,
      }),
    );
  };

  const options: SpaceDiscriminator[] = useMemo(() => {
    if (item == null || !isDefinedString(discriminator)) {
      return [];
    }

    switch (discriminator) {
      case 'Building':
        return ['Generic', 'CommercialSpace', 'CommonArea', 'ParkingSpot'];
      case 'ResidentialUnit':
        return ['CommonArea', 'Room', 'ParkingSpot'];
      case 'CommercialSpace':
        return ['CommonArea', 'ParkingSpot', 'CommercialSpace'];
      case 'Room':
        return [];
      case 'Generic':
        return ['Generic', 'CommercialSpace', 'CommonArea', 'ParkingSpot'];
      default:
        return [];
    }
  }, [item, discriminator]);

  const hasNoOptions = useMemo(() => options.length <= 0, [options]);

  if (hasNoOptions) {
    return null;
  }

  return (
    <Menu as="div">
      <Menu.Button
        as="div"
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref={(ref: any) => setReferenceElement(ref)}
        onClick={(e: UIEvent) => e.stopPropagation()}
      >
        <IconButton
          color="none"
          size="sm"
          fontSize="md"
          rounding="none"
          className="text-black hover:text-black active:bg-black/[0.10] rounded-lg"
        >
          <BiPlus />
        </IconButton>
      </Menu.Button>
      <Portal>
        <div ref={popperElRef} style={styles['popper']} className="z-40" {...attributes['popper']}>
          <Transition
            as={Fragment}
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
            beforeEnter={() => setPopperElement(popperElRef.current)}
            afterLeave={() => setPopperElement(null)}
          >
            <Menu.Items
              static
              className="mt-2 w-56 divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
            >
              <div className="p-1">
                {options.map((option) => (
                  <Menu.Item key={option}>
                    <button
                      className="text-gray-900 group flex w-full items-center rounded-md px-2 py-2 text-sm hover:bg-brand-primary hover:text-black text-left"
                      onClick={(e) => handleSelectOption(e, option)}
                    >
                      {tType(`SpaceDiscriminator.${option}`)}
                    </button>
                  </Menu.Item>
                ))}
              </div>
            </Menu.Items>
          </Transition>
        </div>
      </Portal>
    </Menu>
  );
}
